import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Phone, Mail, AlertCircle, LinkIcon } from "lucide-react";
import CallbackHelp from "@/components/CallbackHelp";
import JourneyHeader from "@/components/JourneyHeader";

const JourneyError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  
  // Get error type from URL parameter
  const errorType = searchParams.get('type') || 'generic';
  
  // Define error messages based on type
  const errorMessages = {
    'invalid_source': {
      title: 'Link Expired or Invalid',
      description: 'The journey link you used is invalid or has expired. Please return to the source page and try again.',
      errorCode: 'INVALID_SOURCE',
      icon: <LinkIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />,
    },
    'missing_source': {
      title: 'Link Missing or Corrupted',
      description: 'The journey link is missing or corrupted. Please return to the source page and start a new application.',
      errorCode: 'MISSING_SOURCE',
      icon: <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />,
    },
    'generic': {
      title: 'Something went wrong',
      description: 'We encountered an issue processing your request. Please try again or contact our support team for assistance.',
      errorCode: 'JOURNEY_SYNC_ERROR',
      icon: <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />,
    },
  };
  
  const error = errorMessages[errorType] || errorMessages['generic'];

  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />

      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[calc(100vh-100px)]">
        <Card className="w-full max-w-lg shadow-lg border-red-100">
          <CardHeader className="text-center">
            {error.icon}
            <CardTitle className="text-2xl font-bold text-red-600">{error.title}</CardTitle>
            <CardDescription className="text-base mt-2">
              {error.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-red-50 p-4 rounded-md border border-red-100 text-center text-red-800 text-sm">
              Error Code: {error.errorCode}
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setIsCallbackOpen(true)}
                className="w-full bg-intellend-green hover:bg-intellend-green/90 py-6 text-lg"
              >
                <Mail className="mr-2 h-5 w-5" />
                Request Callback
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="w-full py-6 text-lg"
              >
                Return to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <CallbackHelp
        isOpen={isCallbackOpen}
        setIsOpen={setIsCallbackOpen}
        showOk={false}
        showCallUs={true}
        showWhatsappUs={true}
        silentlyTrigger={true}
        title="Intellend Support"
      />
    </div>
  );
};

export default JourneyError;
